import type { TrialLength, UpgradeId } from '../../core/balance';
import {
  autowalk,
  buy as buyUpgrade,
  recordTrial,
  tap as tapEngine,
  toggleDirection,
  type GameEvent,
} from '../../core/engine';
import { deserialize, fromSaveCode, serialize, toSaveCode } from '../../core/save';
import { newGame, type GameState } from '../../core/state';
import { storageKey } from './edition';
import { storage } from './storage';

export type EventSource = 'tap' | 'auto';
export type GameListener = (events: readonly GameEvent[], source: EventSource) => void;

const SAVE_KEY = storageKey('save');
const SAVE_EVERY_MS = 2000;

export type TrialPhase = 'off' | 'armed' | 'running' | 'done';

/**
 * The live game: reactive state for the UI, an event stream for sound and animation,
 * the frame loop for auto-walk, and throttled saving.
 */
class Game {
  s = $state<GameState>(this.#load());

  trial = $state({
    phase: 'off' as TrialPhase,
    length: 10 as TrialLength,
    taps: 0,
    startedAt: 0,
    remainingMs: 0,
    isBest: false,
  });

  #listeners = new Set<GameListener>();
  #carry = 0;
  #pendingPlayMs = 0;
  #dirty = false;
  #lastSave = 0;

  #load(): GameState {
    const raw = storage.get(SAVE_KEY);
    return (raw && deserialize(raw)) || newGame();
  }

  on(fn: GameListener): () => void {
    this.#listeners.add(fn);
    return () => this.#listeners.delete(fn);
  }

  #emit(events: readonly GameEvent[], source: EventSource): void {
    if (events.length === 0) return;
    for (const fn of this.#listeners) fn(events, source);
  }

  tap(): void {
    if (this.trial.phase === 'armed') {
      this.trial.phase = 'running';
      this.trial.startedAt = performance.now();
      this.trial.remainingMs = this.trial.length * 1000;
    }
    if (this.trial.phase === 'running') this.trial.taps++;
    this.#emit(tapEngine(this.s), 'tap');
    this.#dirty = true;
  }

  /** Called once per animation frame with elapsed ms. */
  frame(dtMs: number, now: number): void {
    // Clamp long gaps (tab in background) so auto-walk doesn't jump hundreds of steps.
    const dt = Math.min(dtMs, 250);
    const events: GameEvent[] = [];
    this.#carry = autowalk(this.s, dt, this.#carry, events);
    if (events.length > 0) {
      this.#emit(events, 'auto');
      this.#dirty = true;
    }

    this.#pendingPlayMs += dt;
    if (this.#pendingPlayMs >= 1000) {
      this.s.playTimeMs += this.#pendingPlayMs;
      this.#pendingPlayMs = 0;
      this.#dirty = true;
    }

    if (this.trial.phase === 'running') {
      const left = this.trial.length * 1000 - (now - this.trial.startedAt);
      this.trial.remainingMs = Math.max(0, left);
      if (left <= 0) this.#finishTrial();
    }

    if (this.#dirty && now - this.#lastSave > SAVE_EVERY_MS) this.save(now);
  }

  buy(id: UpgradeId): number | null {
    const level = buyUpgrade(this.s, id);
    if (level !== null) this.save();
    return level;
  }

  flip(): boolean {
    const ok = toggleDirection(this.s);
    if (ok) this.#dirty = true;
    return ok;
  }

  armTrial(length: TrialLength): void {
    Object.assign(this.trial, {
      phase: 'armed',
      length,
      taps: 0,
      remainingMs: length * 1000,
      isBest: false,
    });
  }

  closeTrial(): void {
    this.trial.phase = 'off';
  }

  #finishTrial(): void {
    this.trial.isBest = recordTrial(this.s, this.trial.length, this.trial.taps);
    this.trial.phase = 'done';
    this.save();
  }

  save(now = performance.now()): void {
    storage.set(SAVE_KEY, serialize(this.s));
    this.#dirty = false;
    this.#lastSave = now;
  }

  exportCode(): string {
    return toSaveCode(this.s);
  }

  importCode(code: string): boolean {
    const next = fromSaveCode(code);
    if (!next) return false;
    this.s = next;
    this.save();
    return true;
  }

  reset(): void {
    this.s = newGame();
    this.#carry = 0;
    this.trial.phase = 'off';
    this.save();
  }
}

export const game = new Game();
